import { Device } from 'types';
import styles from '../DeviceMap.module.scss';

import dotError from 'assets/icons/dot-error.png';
import dotChartreuse from 'assets/icons/dot-chartreuse.png';
import dotYellow from 'assets/icons/dot-yellow.png';
import dotOrange from 'assets/icons/dot-orange.png';
import dotRed from 'assets/icons/dot-red.png';
import dotGreen from 'assets/icons/dot-green.png';
import {
    Box,
    IconButton,
    ListItemButton,
    Typography,
    useTheme,
} from '@mui/material';
import { getDateWithTime } from 'helpers/dates';
import { ArrowRightAltRounded } from '@mui/icons-material';
import { NavigateFunction } from 'react-router-dom';
import { formatDecimalToFixedPercent } from 'helpers';
import { alpha } from '@mui/material';

interface RenderRowProps {
    device: Device;
    mapRef: google.maps.Map | undefined;
    navigate: NavigateFunction;
    style: React.CSSProperties;
    /** The currently focused/selected device. */
    selectedDevice: Device | null;
    /** Callback to alert parent component that a device has been focused/selected by the user. */
    onDeviceSelected: React.Dispatch<React.SetStateAction<Device | null>>;
    selectedMarkerId: string | null;
    setSelectedMarkerId: React.Dispatch<React.SetStateAction<string | null>>;
}

export function DeviceMapRow({
    device,
    mapRef,
    navigate,
    style,
    selectedDevice,
    onDeviceSelected,
    selectedMarkerId,
    setSelectedMarkerId,
}: RenderRowProps) {
    const theme = useTheme();

    const onDeviceArrowClick = (
        e: React.MouseEvent<HTMLButtonElement>,
        deviceId: string
    ) => {
        e.stopPropagation();
        e.preventDefault();

        navigate(`/device-details/${deviceId}`);
    };

    const getDateIconUrl = function (lastReading: Device['lastReading']) {
        const now = new Date().getTime();

        const dateError = !lastReading || now - lastReading.time > 86400000 * 3;

        if (dateError) {
            return dotError;
        }

        return dotChartreuse;
    };

    const getTankIcon = (device: Device) => {
        const now = new Date().getTime();

        if (!device.estimated_tank || !device.lastReading?.time) {
            return dotError;
        }

        const deviceError = now - device.lastReading.time > 86400000 * 3;

        if (deviceError) {
            return dotError;
        }

        switch (true) {
            case device.estimated_tank >= 80:
                return dotGreen;
            case device.estimated_tank < 80 && device.estimated_tank >= 60:
                return dotChartreuse;
            case device.estimated_tank < 60 && device.estimated_tank >= 40:
                return dotYellow;
            case device.estimated_tank < 40 && device.estimated_tank >= 20:
                return dotOrange;
            case device.estimated_tank < 20:
                return dotRed;
            default:
                return dotGreen;
        }
    };

    if (!device) return <div />;

    const imgUrl = getTankIcon(device);

    return (
        <ListItemButton
            id={device.deviceId}
            key={device.deviceId}
            style={style}
            sx={{
                backgroundColor:
                    device.deviceId === selectedMarkerId ||
                    device.deviceId === selectedDevice?.deviceId
                        ? theme.palette.primary.main
                        : 'unset',
                '&:hover': {
                    backgroundColor:
                        device.deviceId === selectedDevice?.deviceId
                            ? alpha(theme.palette.primary.main, 0.8)
                            : 'unset',
                },
            }}
            onClick={() => {
                setSelectedMarkerId(null);
                onDeviceSelected(device);
                mapRef?.panTo({
                    lat: device.lat,
                    lng: device.lng,
                });
            }}
        >
            <Box className={styles.listItemContent}>
                <Box className={styles.overflowContainer}>
                    <Typography
                        variant="subtitle2"
                        data-testid="DeviceName"
                        fontWeight="bold"
                    >
                        {device.name || device.shortDeviceId || device.deviceId}
                        {/* {device.deviceId} */}
                    </Typography>
                </Box>
                <Box className={styles.overflowContainer}>
                    <Typography variant="subtitle2" data-testid="DeviceAddress">
                        {device.address?.trim().length
                            ? device.address
                            : 'Address unavailable'}
                    </Typography>
                </Box>
                <div className={styles.listItemSection}>
                    <img src={imgUrl} alt={'device tank status'} />
                    <Typography variant="subtitle1" data-testid="DeviceName">
                        {device.estimated_tank
                            ? formatDecimalToFixedPercent(device.estimated_tank)
                            : 'N/A'}
                    </Typography>
                </div>
                <div className={styles.listItemSection}>
                    <img
                        src={getDateIconUrl(device.lastReading)}
                        alt={'monitor last reading status'}
                    />
                    <Typography variant="subtitle1" data-testid="DeviceName">
                        {device.lastReading?.time
                            ? getDateWithTime(device.lastReading.time)
                            : 'N/A'}
                    </Typography>
                </div>
            </Box>
            <div className={styles.navigateArrow}>
                <IconButton
                    onClick={(e) => onDeviceArrowClick(e, device.deviceId)}
                    color="secondary"
                >
                    <ArrowRightAltRounded fontSize="large" />
                </IconButton>
            </div>
        </ListItemButton>
    );
}
